import {
  cloneElement,
  memo,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTheme } from "@emotion/react";
import { Box, Divider, Container, ListItem, useMediaQuery } from "@mui/material";
import styled from "@emotion/styled";
import CustomMenu from "../CustomMenu";
import HideableMenuButton from "../HideableMenuButton";

const styles = {
  visible: {
    display: 'inline-flex',
  },
  hidden: {
    position: 'absolute',
    top: -9999,
    left: -9999,
  }
};

const SideElementContainer = styled(Box)`
  display: flex;
  position: relative;
  z-index: 2;

  &:nth-of-type(2) {
    margin-left: auto;
  }
`;

const CenterElementContainer = styled(Container)`
  padding: 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  margin: 0 auto;
  justify-content: center;
`;

const CustomListItem = styled(ListItem)`
  width: 100%;
  > [class*=MuiButton] {
    width: 100%;
    justify-content: left;
  }
`;

const ResponsiveMenu = memo(
  ({ responsiveItems = [], menuItems = [], ...restProps }) => {
    const [visibleCount, setVisibleCount] = useState(responsiveItems.length);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const sideElementsRef = useRef();
    const containerRef = useRef();
    const menuData = useRef();
    const theme = useTheme();
    const {startElement, endElement, closeElement} = restProps;
    const isScreenSm = useMediaQuery(theme => theme.breakpoints.down('md'));
    
    /** --- Spazio laterale (virtuale) del menu
     * previene una sovrapposizione esatta con gli elementi ai lati --- */
    const MENU_ADDITIONAL_PADDING = theme.spacing(2);
    
    useEffect(
      () => {
        
        let items = getItemsWidth();
        menuData.current = {
          items,
          menu: items.reduce((a, b) => a + b, 0) + parseFloat(MENU_ADDITIONAL_PADDING),
        };

        (async () => {

          /** 
           * get the visible count for the first time 
           */
          await calculateVisibleCount();
        })()

        /** 
         * container is initially invisible to prevent glitches
         * caused by hiding/showing menu-items
         */
        containerRef.current.style.opacity = 1;

        if (visibleCount === responsiveItems.length) {
          setMenuAnchor(null);
        }

        window.addEventListener('resize', calculateVisibleCount);
        return () => {
          window.removeEventListener('resize', calculateVisibleCount);
        }
      }, [visibleCount, setMenuAnchor]
    );

    /** --- mapping delle dimensioni degli items --- */
    const getItemsWidth = () => {
      return Array.from(containerRef.current.children).map((child) => {
        const childStyles = window.getComputedStyle(child);
        const {width} = child.getBoundingClientRect();
        const {marginLeft, marginRight, paddingLeft, paddingRight} = childStyles;
        return width + parseFloat(marginLeft) + parseFloat(marginRight) + parseFloat(paddingLeft) + parseFloat(paddingRight);
      });
    };

    /** --- algo per lo spazio rimasto vuoto --- */
    const getAvailableSpace_v2 = (newMenuWidth) => {
      let span = (containerRef.current.parentElement.clientWidth - newMenuWidth) / 2;
      return Math.min(
        span - sideElementsRef.current.firstElementChild.offsetWidth, 
        span - sideElementsRef.current.lastElementChild.offsetWidth
      );
    };

    /** --- calcolo della quantità di items da visualizzare --- */
    const calculateVisibleCount = async () => {
      let availableSpace = getAvailableSpace_v2(menuData.current.menu);
      let newVisibleCount = menuData.current.items.length;
      let cumulatedWidth = 0;
      while(availableSpace < 0) {
        cumulatedWidth += menuData.current.items[newVisibleCount-1];
        availableSpace = getAvailableSpace_v2(menuData.current.menu - cumulatedWidth);
        newVisibleCount--;
      }
      if (newVisibleCount !== visibleCount) {
        setVisibleCount(newVisibleCount);
      }
    };

    const onMenuClose = () => setMenuAnchor(null);
    const onClickMenuButton = (event) => setMenuAnchor(event.target);
    const menuButtonIsVisible = (visibleCount < responsiveItems.length) || menuItems.length > 0 || isScreenSm;

    return (
      <Container sx={{ p: 0, position: 'relative' }}>
        {/* ----- Elementi laterali ----- */}
        <Box ref={sideElementsRef} display="flex">
          {/* ----- Elementi a sinistra ----- */}
          <SideElementContainer>
            {startElement}
            <Box sx={{ ml: 2, display: { xs: 'none', md: 'inline-flex' }}}>
              {cloneElement(closeElement)}
            </Box>
          </SideElementContainer>
          {/* ----- Elementi a destra ----- */}
          <SideElementContainer>
            {endElement}
            <HideableMenuButton isVisible={menuButtonIsVisible} onClick={onClickMenuButton}>
              Menu
            </HideableMenuButton>
            <CustomMenu
              open={Boolean(menuAnchor)}
              onClose={onMenuClose}
              anchorEl={menuAnchor}
            >
              {responsiveItems.slice(visibleCount).map((item, index) => 
                <CustomListItem key={index} disablePadding>{item}</CustomListItem>
              )}
              {visibleCount<responsiveItems.length && <Divider />}
              {menuItems.map((item, index) => 
                <CustomListItem key={index} disablePadding>{item}</CustomListItem>
              )}
              <CustomListItem disablePadding>{cloneElement(closeElement)}</CustomListItem>
            </CustomMenu>
          </SideElementContainer>
        </Box>
        {/* ----- Elementi centrali ----- */}
        <CenterElementContainer>
          {responsiveItems.length >= 0 && (
            <Box ref={containerRef} display="flex" sx={{ opacity: 0, d: 'flex', alignItems: 'center', gap: 1 }}>
              {responsiveItems.map((item, index) => 
                cloneElement(item, { 
                  key: index, 
                  sx: visibleCount-1 >= index ? styles.visible : styles.hidden
                })
              )}
            </Box>
          )}
        </CenterElementContainer>
      </Container>
    );
  }
);

export default ResponsiveMenu;